import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, AlertTriangle, ArrowUp, FileText } from 'lucide-react';
import { useDismissNotification, useNotifications } from '@/features/agent-actions/hooks/useAgentActions';
import type { PortalNotification } from '@/features/agent-actions/types';

function formatRelativeTime(value: string) {
  const diffMs = Date.now() - new Date(value).getTime();
  if (diffMs <= 0) return 'just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function expiresIn(expiresAt?: string) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const min = Math.ceil(ms / 60_000);
  return `expires in ${min} min`;
}

function NotificationIcon({ type }: { type: PortalNotification['type'] }) {
  if (type === 'template_proposal_pending') return <FileText className="w-4 h-4 text-purple-600" />;
  if (type === 'promotion_pending') return <ArrowUp className="w-4 h-4 text-blue-600" />;
  return <AlertTriangle className="w-4 h-4 text-amber-600" />;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const notifs = useNotifications();
  const dismiss = useDismissNotification();
  const items = notifs.data?.notifications || [];
  const count = notifs.data?.unread_count || 0;

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleClick = (n: PortalNotification) => {
    setOpen(false);
    if (n.action_url.startsWith('/')) navigate(n.action_url);
    else window.location.href = n.action_url;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-lg z-50"
          style={{ boxShadow: 'var(--shadow-card-hover)' }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            <button type="button" onClick={() => setOpen(false)} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {!items.length ? (
              <div className="px-4 py-8 text-sm text-gray-500 text-center">
                No notifications. Everything&apos;s quiet.
              </div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5"><NotificationIcon type={n.type} /></div>
                    <button type="button" className="flex-1 text-left min-w-0" onClick={() => handleClick(n)}>
                      <div className="text-sm font-medium text-gray-900">{n.title}</div>
                      <div className="text-xs text-gray-600 mt-0.5">{n.subtitle}</div>
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-400">
                        <span>{formatRelativeTime(n.created_at)}</span>
                        {n.expires_at && <span>{expiresIn(n.expires_at)}</span>}
                      </div>
                    </button>
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:text-gray-800 shrink-0"
                      onClick={() => dismiss.mutate(n.id)}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
